import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@app/store';

/** Allowed dashboard date-range windows (days back). Mirrors the
 * ButtonMenu.../Dashboard/index.js. */
export const DASHBOARD_DATE_RANGES = [7, 30, 90] as const;
export type DashboardDateRange = (typeof DASHBOARD_DATE_RANGES)[number];

/** Dashboard merchant-filter selection (super-admins / parent-merchants).
 * `label` rides along so the combobox can render the picked option without an
 * extra fetch; `isParent` so onboarding-step clicks can seed the view-as scope
 * with the right parent/sub flag. An empty `id` means "All Merchants". */
export interface DashboardMerchantFilter {
  id: string;
  label: string | null;
  isParent: boolean;
}

export const DASHBOARD_MERCHANT_FILTER_NONE: DashboardMerchantFilter = {
  id: '',
  label: null,
  isParent: false,
};

interface InsightUIState {
  /** Days back from today the dashboard charts cover. Defaults to 30,
 * matching the reducer's initial state. */
  dashboardDateRange: DashboardDateRange;
  /** Lives here (not component state) so the selection survives navigating
 * away and back — e.g. onboarding card → detail page → back arrow. */
  dashboardMerchantFilter: DashboardMerchantFilter;
}

const initialState: InsightUIState = {
  dashboardDateRange: 30,
  dashboardMerchantFilter: DASHBOARD_MERCHANT_FILTER_NONE,
};

const insightSlice = createSlice({
  name: 'insightUI',
  initialState,
  reducers: {
    updateDashboardDateRange(state, action: PayloadAction<DashboardDateRange>) {
      state.dashboardDateRange = action.payload;
    },
    updateDashboardMerchantFilter(state, action: PayloadAction<DashboardMerchantFilter>) {
      state.dashboardMerchantFilter = action.payload;
    },
  },
});

export const { updateDashboardDateRange, updateDashboardMerchantFilter } = insightSlice.actions;
export const insightReducer = insightSlice.reducer;

export const selectDashboardDateRange = (state: RootState): DashboardDateRange =>
  state.insightUI.dashboardDateRange;

export const selectDashboardMerchantFilter = (state: RootState): DashboardMerchantFilter =>
  state.insightUI.dashboardMerchantFilter;
